import CTNTabNavigation from '@ctn/ui/components/ctn-tab-navigation';
import { type CTNButtonProps } from '@ctn/ui/types/ctn-button';
import { type CTNTabContentProps, type CTNTabNavigationProps } from '@ctn/ui/types/ctn-tab';
import { type ArrayKey } from '@ctn/ui/types/generic';
import { useMemo, useState, type ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import PageHeader from '@/components/PageHeader';

type PageTabsProps = {
	readonly tabs: Array<CTNTabContentProps & Pick<ArrayKey, 'key'> & { assignUrl?: string }>;
	readonly defaultActive: string;
	readonly title: string;
	readonly hasHelp?: boolean;
	readonly actions?: CTNButtonProps[];
	readonly onTabChange?: (key: string) => void;
};

/**
 * Componente de navegación por pestañas con soporte para enrutamiento dinámico.
 * * Este componente sincroniza visualmente las pestañas con la URL del navegador.
 * - Si la pestaña tiene un `assignUrl`, la navegación se realiza mediante la API del historial (sin recargar).
 * - Si no tiene `assignUrl`, se maneja como una pestaña de estado interno.
 */
const PageTabs = ({ tabs, defaultActive, title, hasHelp, actions, onTabChange }: PageTabsProps): ReactElement => {
	const navigate = useNavigate();
	const location = useLocation();
	const [selectedId, setSelectedId] = useState<string>(defaultActive);

	const selectedTab = useMemo(() => {
		const tabByUrl = tabs.find((t) => t.assignUrl === location.pathname);
		if (tabByUrl) return tabByUrl;

		return tabs.find((t) => t.key === selectedId) ?? tabs[0];
	}, [location.pathname, selectedId, tabs]);

	const handleTabChange: CTNTabNavigationProps['onTabChange'] = ({ index }) => {
		const targetTab = tabs.find((t) => t.key === index);

		if (onTabChange) {
			onTabChange(index);
		}

		if (targetTab?.assignUrl) {
			navigate(targetTab.assignUrl);
		} else {
			setSelectedId(index);
		}
	};

	return (
		<>
			<PageHeader title={title} actions={actions} hasHelp={hasHelp} className="u-mt-8" />
			<CTNTabNavigation
				className="u-mt-24"
				tabs={tabs}
				activeTab={selectedTab?.key}
				onTabChange={handleTabChange}
			/>
			<div className="[ tab-content ] [ u-mt-24 ]">{selectedTab?.content}</div>
		</>
	);
};

export default PageTabs;
